import {Component, OnInit} from '@angular/core';
import {CryptService} from '../services/crypt.service';
import {AuthService} from '../services/auth.service';
import {map, mergeMap, Observable, of, Subscription} from 'rxjs';
import {Router} from '@angular/router';
import {ProcessDTO} from './ProcessDTO';
import {MatSnackBarRef, SimpleSnackBar} from '@angular/material/snack-bar';
import JSZip from 'jszip';
import {catchError} from 'rxjs/operators';
import {FeaasErrorHandlerService} from '../services/error-handler.service';
import {ErrorService} from '../services/error.service';

enum Crypt {
  encrypt,
  decrypt,
}

@Component({
    selector: 'app-crypt-multiple',
    templateUrl: './crypt-multiple.component.html',
    styleUrls: ['./crypt-multiple.component.css'],
    standalone: false
})
export class CryptMultipleComponent implements OnInit {
  public readonly cryptEnum: typeof Crypt = Crypt;

  public concurrentRequestLimit = 5;
  // File Size in Bytes
  public multipleFileSizeLimit = 1024 * 1024 * 1;
  public multipleFileSelectLimit = 45;
  public multipleFileSelectLimitStr = this.multipleFileSizeLimit / (1024 * 1024) + ' MB';

  public userId: any;
  public userHasProcess = false;
  public downloadHref: string;
  public downloadName: string;

  public processOptions: string[] = [];
  public decryptOptions: string[] = [];
  public encryptOptions: string[] = [];

  public requestLoading = false;
  public requestSubscription: Subscription;

  // User selections
  public selectedCrypt: number;
  public selectedFiles: File[] = [];
  public selectedProcess: string;
  public selectedMultipleFileUpload = false;

  public errorList: {
    fileName: string,
    errorCode: number,
    message: any
  }[] = [];

  public messageRef: MatSnackBarRef<SimpleSnackBar>;

  constructor(private feaasErrorHandler: FeaasErrorHandlerService,
              private cryptService: CryptService,
              private authService: AuthService,
              private errorService: ErrorService,
              private router: Router) {
    this.selectedCrypt = Crypt.encrypt;
  }

  ngOnInit(): void {
    this.getProcesses();
    this.authService.currentUserStatus$.subscribe({
      next: response => {
        this.userId = response?.profile?.uid;
      }
    });
  }

  public cancelRequest(): void {
    this.requestLoading = false;
    if (!this.requestSubscription) {
      return;
    }
    this.requestSubscription.unsubscribe();
  }

  public clearDownload(): void {
    if (this.downloadHref) {
      window.URL.revokeObjectURL(this.downloadHref);
      this.downloadHref = null;
    }
    this.errorList = [];
  }

  public decryptFile(): void {
    this.requestLoading = true;
    this.clearDownload();
    if (this.requestSubscription) {
      this.requestSubscription.unsubscribe();
    }

    const cryptObs: Observable<any>[] = this.selectedFiles.map((file) => {
      return this.cryptService.decryptFile$(this.selectedProcess, file)
        .pipe(map((blob) => new File([blob], this.getDecryptedFileName(file.name))),
          catchError((error) => {
            this.errorList.push({
              fileName: file.name,
              errorCode: error.status,
              message: error.error?.message || error.message
            });
            const errorBlob = new Blob([`File Name: ${file.name}\nProcess: ${this.selectedProcess}\nUser: ${this.userId}\nTimestamp: ${error.headers?.get('Date')}\nError Code: ${error.status}\nError Message: ${error.error?.message || error.message}`], {type: 'text/plain;charset=utf-8'});
            const errorFile = new File([errorBlob], file.name + '_ERROR.txt');
            return of(errorFile);
          }),
        );
    });
    if (this.selectedMultipleFileUpload) {
      // Resulting files must be packaged in a zip file
      const zip = new JSZip();

      this.requestSubscription = of(...cryptObs).pipe(mergeMap((apiCall) => apiCall, this.concurrentRequestLimit)).subscribe({
          next: response => {
            zip.file(response.name, response);
          },
          error: error => {
            this.requestLoading = false;
            this.feaasErrorHandler.handleError(error);
          },
          complete: () => {
            // Create zip file and set
            this.downloadName = this.selectedProcess + '.zip';
            zip.generateAsync({type: 'blob'}).then((blob) => {
              this.downloadHref = window.URL.createObjectURL(blob);
              this.requestLoading = false;
            }).catch((error) => {
              this.requestLoading = false;
              this.feaasErrorHandler.handleError(error);
            });
          }
        }
      );
    } else if (cryptObs.length === 1) {
      this.requestSubscription = cryptObs[0].subscribe({
          next: response => {
            this.downloadName = response.name;
            this.downloadHref = window.URL.createObjectURL(response);
          },
          error: error => {
            this.requestLoading = false;
            this.feaasErrorHandler.handleError(error);
          },
          complete: () => {
            this.requestLoading = false;
          }
        }
      );
    }
  }

  public encryptFile(): void {
    this.requestLoading = true;
    this.clearDownload();
    if (this.requestSubscription) {
      this.requestSubscription.unsubscribe();
    }

    const cryptObs: Observable<any>[] = this.selectedFiles.map((file) => {
      return this.cryptService.encryptFile$(this.selectedProcess, file)
        .pipe(map((blob) => new File([blob], file.name + '.encrypted')),
          catchError((error) => {
            this.errorList.push({
              fileName: file.name,
              errorCode: error.status,
              message: error.error?.message || error.message
            });
            const errorBlob = new Blob([`File Name: ${file.name}\nProcess: ${this.selectedProcess}\nUser: ${this.userId}\nTimestamp: ${error.headers?.get('Date')}\nError Code: ${error.status}\nError Message: ${error.error?.message || error.message}`], {type: 'text/plain;charset=utf-8'});
            const errorFile = new File([errorBlob], file.name + '_ERROR.txt');
            return of(errorFile);
          }),
        );
    });
    if (this.selectedMultipleFileUpload) {
      // Resulting files must be packaged in a zip file
      const zip = new JSZip();

      this.requestSubscription = of(...cryptObs).pipe(mergeMap((apiCall) => apiCall, this.concurrentRequestLimit)).subscribe({
          next: response => {
            zip.file(response.name, response);
          },
          error: error => {
            this.requestLoading = false;
            this.feaasErrorHandler.handleError(error);
          },
          complete: () => {
            // Create zip file and set
            this.downloadName = this.selectedProcess + '.zip';
            zip.generateAsync({type: 'blob'}).then((blob) => {
              this.downloadHref = window.URL.createObjectURL(blob);
              this.requestLoading = false;
            }).catch((error) => {
              this.requestLoading = false;
              this.feaasErrorHandler.handleError(error);
            });
          }
        }
      );
    } else if (cryptObs.length === 1) {
      this.requestSubscription = cryptObs[0].subscribe({
          next: response => {
            this.downloadName = response.name;
            this.downloadHref = window.URL.createObjectURL(response);
          },
          error: error => {
            this.requestLoading = false;
            this.feaasErrorHandler.handleError(error);
          },
          complete: () => {
            this.requestLoading = false;
          }
        }
      );
    }
  }

  public getDecryptedFileName(fileName: string): string {
    const lastIndex = fileName.lastIndexOf('.');
    if (lastIndex < 0) {
      return fileName;
    }
    return fileName.slice(0, lastIndex);
  }

  public getProcesses(): void {
    this.cryptService.getProcesses().subscribe({
      next: (response: ProcessDTO[]) => {
        const processes = response;
        const encryptOptions: string[] = [];
        const decryptOptions: string[] = [];

        if (processes.length === 0) {
          this.router.navigateByUrl('/unauthorized');
        }
        this.userHasProcess = true;
        for (const process of processes) {
          if (process.encrypt) {
            encryptOptions.push(process.id);
          }
          if (process.decrypt) {
            decryptOptions.push(process.id);
          }
        }
        this.encryptOptions = encryptOptions;
        this.decryptOptions = decryptOptions;
        if (this.selectedCrypt === this.cryptEnum.encrypt) {
          this.processOptions = this.encryptOptions;
        } else {
          this.processOptions = this.decryptOptions;
        }
      },
      error: error => {
        this.feaasErrorHandler.handleError(error);
        if (error.status !== 401) {
          const status = error.status;
          const message = error.message;
          this.errorService.setError({ status, message });
          this.router.navigateByUrl('/error');
        } else if (this.processOptions.length === 0) {
          this.router.navigateByUrl('/unauthorized');
        }
      }
    });
  }

  public onFileClick(event: any): void {
    event.target.value = '';
    this.selectedFiles = [];
  }

  public onFileSelect(event: any): void {
    const files = [...event.target.files];
    const errorMessages: string[] = [];
    let error = false;
    if (files.length > this.multipleFileSelectLimit) {
      error = true;
      errorMessages.push('File selection limit is ' + this.multipleFileSelectLimit + '.');
    }
    if (this.selectedMultipleFileUpload) {
      for (const file of files) {
        if (file.size > this.multipleFileSizeLimit) {
          error = true;
          errorMessages.push('File size limit is ' + this.multipleFileSelectLimitStr + ' when multiple file upload is selected.');
          break;
        }
      }
    }
    if (error) {
      this.selectedFiles = [];
      event.target.value = '';
      const errorMessage = errorMessages.join(' ');
      this.feaasErrorHandler.handleError(errorMessage);

    } else {
      this.selectedFiles = files;
    }
    this.clearDownload();
  }

  public onSubmit(): void {
    if (this.messageRef) {
      this.messageRef.dismiss();
    }
    if (this.selectedCrypt === Crypt.encrypt) {
      this.encryptFile();
    } else if (this.selectedCrypt === Crypt.decrypt) {
      this.decryptFile();
    }
  }

  public cryptRadioChange(event: any): void {
    if (event.value === this.cryptEnum.encrypt) {
      this.processOptions = this.encryptOptions;
    } else if (event.value === this.cryptEnum.decrypt) {
      this.processOptions = this.decryptOptions;
    }
    if (!this.processOptions.includes(this.selectedProcess)) {
      this.selectedProcess = null;
    }
    this.selectedFiles = [];
    this.clearDownload();
  }

  public fileUploadRadioChange(event: any): void {
    this.selectedFiles = [];
    this.clearDownload();
  }

  /*private displayError(error: any): void {
    let errorMessage;
    if (error instanceof HttpErrorResponse) {
      errorMessage = `Error Code ${error.status}: ${error.error.message}`;
    } else {
      errorMessage = error.message || error;
    }
    this.messageRef = this.fabMessageService.displayErrorMessage(errorMessage, 'Okay');
  }*/

}
